import React, { useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import { MSelect, MButton } from "src/M_Components";
import CarrierResponseDetailTable from "../../components/Tenders/CarrierResponseDetailTable";
import { useTranslation } from "react-i18next";

export default function TenderCarrierDetailView({
  tenderInfo,
  phases,
  quotes,
  carriers,
  lanes,
  isSpotTender,
  selectedCarrierId,
  onBack,
  onRefresh,
  selectedPhaseId,
  setSelectedPhaseId,
}) {
  const { t } = useTranslation();

  // Selected Carrier
  const [selectedCarrier, setSelectedCarrier] = useState(selectedCarrierId);

  //============================================================================================================
  // Filters carriers that had submitted quotes
  //============================================================================================================
  const carriersWithQuotes = useMemo(() => {
    const safeQuotes = Array.isArray(quotes) ? quotes : [];
    const safeCarriers = Array.isArray(carriers) ? carriers : [];

    if (isSpotTender) {
      const quotedCarrierIds = new Set(
        safeQuotes.filter((q) => !q?.Inactive).map((q) => q?.CarrierId)
      );
      return safeCarriers.filter((c) =>
        quotedCarrierIds.has(c?.CarrierId || c?.Id)
      );
    }

    // Non-Spot: if a phase is selected, filter carriers to those that have quotes in that phase
    if (selectedPhaseId) {
      const quotedCarrierIds = new Set(
        safeQuotes
          .filter((q) => !q?.Inactive && q?.TenderPhaseId === selectedPhaseId)
          .map((q) => q?.CarrierId)
      );
      return safeCarriers.filter((c) =>
        quotedCarrierIds.has(c?.CarrierId || c?.Id)
      );
    }

    // Fallback: any carrier that has any quote
    const quotedCarrierIds = new Set(
      safeQuotes.filter((q) => !q?.Inactive).map((q) => q?.CarrierId)
    );
    return safeCarriers.filter((c) =>
      quotedCarrierIds.has(c?.CarrierId || c?.Id)
    );
  }, [carriers, quotes, isSpotTender, selectedPhaseId]);

  //============================================================================================================
  // EFFECT
  // When the selected carrier changes, update the state
  //============================================================================================================
  useEffect(() => {
    setSelectedCarrier(selectedCarrierId ?? null);
  }, [selectedCarrierId]);

  //============================================================================================================
  // Filter quotes based on the selected phase for non-spot tenders
  //============================================================================================================
  const filteredQuotes = useMemo(() => {
    const safeQuotes = Array.isArray(quotes) ? quotes : [];
    if (isSpotTender) return safeQuotes;

    if (!selectedPhaseId) return safeQuotes; // if none selected, show all (or keep empty if you prefer)
    return safeQuotes.filter((q) => q?.TenderPhaseId === selectedPhaseId);
  }, [quotes, isSpotTender, selectedPhaseId]);

  return (
    <div
      style={{
        padding: "20px",
        display: "flex",
        flexDirection: "column",
        gap: "20px",
      }}
    >
      <div
        style={{
          marginTop: "10px",
          display: "grid",
          padding: "20px",
          gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
          maxHeight: "250px",
          gap: "20px",
        }}
      >
        {/* Only Selects Phase if Tender is Non-Spot */}
        {!isSpotTender && (
          <MSelect
            label={t("Phase")}
            value={
              (Array.isArray(phases) ? phases : [])
                .map((phase) => ({
                  label: phase?.Name || `${t("Phase")} ${phase?.Id}`,
                  value: phase?.Id,
                }))
                .find((opt) => opt.value === selectedPhaseId) || null
            }
            onChangeValue={(newValue) => {
              const next =
                newValue && typeof newValue === "object"
                  ? newValue.value
                  : null;

              // IMPORTANT: lift phase selection to parent so it can refetch lanes/quotes/carriers
              if (typeof setSelectedPhaseId === "function") {
                setSelectedPhaseId(next);
              }
            }}
            options={(Array.isArray(phases) ? phases : []).map((phase) => ({
              label: phase?.Name || `${t("Phase")} ${phase?.Id}`,
              value: phase?.Id,
            }))}
            autocomplete
            required
          />
        )}

        <MSelect
          label={t("Carrier")}
          value={
            carriersWithQuotes
              .map((c) => ({
                label: c?.CarrierName || c?.Name,
                value: c?.CarrierId || c?.Id,
              }))
              .find((opt) => opt.value === selectedCarrier) || null
          }
          onChangeValue={(newValue) => {
            if (newValue && typeof newValue === "object") {
              setSelectedCarrier(newValue.value);
            } else {
              setSelectedCarrier(null);
            }
          }}
          options={carriersWithQuotes.map((c) => ({
            label: c?.CarrierName || c?.Name,
            value: c?.CarrierId || c?.Id,
          }))}
          autocomplete
          required
        />
      </div>

      {/* Carrier Response Details Table */}
      <CarrierResponseDetailTable
        tenderInfo={tenderInfo}
        selectedCarrier={selectedCarrier}
        selectedPhaseId={selectedPhaseId}
        quotes={filteredQuotes}
        lanes={lanes}
        isSpotTender={isSpotTender}
        onRefresh={() => onRefresh(selectedCarrier, selectedPhaseId)}
      />
    </div>
  );
}

TenderCarrierDetailView.propTypes = {
  tenderInfo: PropTypes.object,
  phases: PropTypes.array,
  quotes: PropTypes.array,
  carriers: PropTypes.array,
  lanes: PropTypes.object,
  isSpotTender: PropTypes.bool,
  selectedCarrierId: PropTypes.number,
  onBack: PropTypes.func,
  onRefresh: PropTypes.func,
  selectedPhaseId: PropTypes.number,
  setSelectedPhaseId: PropTypes.func,
};
